import { createSupabaseAdminClient } from "../../supabase/supabase";
import { logException } from "@punchup/sentry";

/**
 * The number of distinct paid subscription tiers the comedian offers (the plans
 * shown on the /[comedianSlug]/subscribe page). Drives the comedian-page CTAs:
 * `> 0` means plans exist (Subscribe / Upgrade / manage menu); `> 1` means there
 * is more than one tier to switch between (the "Change plan" menu item).
 *
 * Degrades to 0 on a transient DB error so the comedian landing page never
 * crashes for a purely cosmetic CTA.
 */
export async function getComedianSubscriptionTierCount(
  comedianId: string
): Promise<number> {
  try {
    const supabase = createSupabaseAdminClient();
    const is_production = process.env.VERCEL_ENV === "production";

    const { data } = await supabase
      .from("products")
      .select("subscription_tier")
      .eq("comedian_id", comedianId)
      .eq("category", "subscription")
      .eq("is_production", is_production)
      .throwOnError();

    const tiers = new Set((data ?? []).map((p) => p.subscription_tier ?? 0));
    return tiers.size;
  } catch (error) {
    // Fails open to 0, so this log is the only signal the query ever broke —
    // include context to keep the Sentry event actionable.
    logException(error as Error, {
      data: `getComedianSubscriptionTierCount: ${comedianId}`,
    });
    return 0;
  }
}
